import { createSafePreviewUrl } from "@/utils/filePreview";
import type React from "react";
import { useId, useMemo } from "react";
import { CheckCircle, Eye, X } from "lucide-react";
import { buildClassName } from "../profile/InputBase";

// Mirrors UploadCard.tsx's formatting so the optional success card below reads the same way.
function formatFileSize(bytes: number): string
{
    if (bytes < 1024) return `${bytes} B`;
    const units = ["KB", "MB", "GB"];
    let value = bytes / 1024;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1)
    {
        value /= 1024;
        unitIndex++;
    }
    return `${value.toFixed(1)} ${units[unitIndex]}`;
}

function formatUploadedAt(isoDate: string): string
{
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return "";
    return `Uploaded ${date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}`;
}

export interface IFileUploadData
{
    filename?: string;
    file?: File | null;
    alreadyUploaded?: boolean;
    filePicked?: boolean;
}

const FileUploadDataDefaults: IFileUploadData =
{
    filename: "",
    file: null,
    alreadyUploaded: false,
    filePicked: false
}

export class FileUploadData implements IFileUploadData
{
    filename: string;
    file: File | null;
    alreadyUploaded: boolean;
    filePicked: boolean;

    constructor(data?: IFileUploadData | null)
    {
        if (data === null || typeof data === "undefined")
        {
            data = FileUploadDataDefaults;
        }
        else
        {
            data = { ...FileUploadDataDefaults, ...data };
        }

        this.filename = data.filename ?? "";
        this.file = data.file ?? null;
        this.alreadyUploaded = data.alreadyUploaded ?? false;

        this.filePicked = this.alreadyUploaded || (this.filename.length > 0);
    }
}

export type FileUploadStateHandler = (state: FileUploadData) => void;

export interface IFileUploadParameters
{
    id?: string;
    className?: string;
    accept?: string;
    fieldLabel: React.ReactNode;
    uploadData: FileUploadData;
    updateState: FileUploadStateHandler;
    droppable?: boolean;
    disabled?: boolean;

    inputClassOverride?: string;
    inputClassAppend?: string;

    // When true, the "already picked" state renders a green success card (checkmark, filename, file size, and upload date)
    showSuccessCard?: boolean;
    accessibleFieldName?: string;
    uploadedAt?: string | null;
    fileSizeBytes?: number | null;
    // URL that streams the active document's bytes, used for Preview when there is no local file object (browser file input) to build a blob URL from.
    previewUrl?: string | null;
}

const FileUpload: React.FC<IFileUploadParameters> = ({
    id,
    className,
    accept,
    fieldLabel,
    uploadData,
    updateState,
    droppable = false,
    disabled = false,
    inputClassOverride = "newprofile-form-input",
    inputClassAppend,
    showSuccessCard = false,
    accessibleFieldName,
    uploadedAt,
    fileSizeBytes,
    previewUrl}) =>
{
    //We don't create state here - it must be handled by the parent control
    const defaultId = useId();
    id = id ?? defaultId;

    const inputClassName = useMemo(
        () => buildClassName(inputClassOverride, inputClassAppend),
        [inputClassOverride, inputClassAppend]);

    accept = accept ?? ".pdf,.jpg,.jpeg,.png";

    const deleteFile = (e: React.MouseEvent) =>
    {
        e.preventDefault();

        updateState({
            ...uploadData,
            filename: "",
            file: null,
            filePicked: false
        });
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    {
        const
            {
                value, //string - name of file?
                files //FileList | null
            } = e.target;

        if (files === null)
        {
            return;
        }

        const file = files[0];

        updateState({
            ...uploadData,
            filename: value,
            file: file,
            filePicked: true
        });
    };

    const handleDrop: React.DragEventHandler<HTMLDivElement> =
        (e: React.DragEvent<HTMLDivElement>) =>
        {
            const items: DataTransferItem[] = [...e.dataTransfer.items].filter(
                (item) => item.kind === "file"
            );

            if (items.length === 0)
            {
                return;
            }
            e.preventDefault();

            e.dataTransfer.dropEffect = "copy";

            const file: File | null = items[0].getAsFile();

            if (file === null)
            {
                return;
            }

            updateState({
                ...uploadData,
                filename: file.name,
                file: file,
                filePicked: true
            });
        };

    const displayName = uploadData.filename.replace("C:\\fakepath\\", "");

    const handlePreview = () =>
    {
        if (uploadData.file)
        {
            const url = createSafePreviewUrl(uploadData.file);
            if (url) window.open(url, "_blank", "noopener,noreferrer");
            return;
        }
        if (previewUrl)
        {
            window.open(previewUrl, "_blank", "noopener,noreferrer");
        }
    };

    return (
        (
            uploadData.filePicked &&
            (showSuccessCard ? (
                <div
                    className="border border-[#bbf7d0] rounded-[10px] px-3.5 py-3 flex items-center gap-3 w-full"
                    style={{ background: "#F0FDF4" }}
                >
                    <CheckCircle className="h-5 w-5 text-green-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <p className="text-[13px] text-[#14532D] font-semibold truncate" title={displayName}>
                            {displayName}
                        </p>
                        <p className="text-xs text-[#3F7A52]">
                            {(() => {
                                const sizeBytes = uploadData.file?.size ?? fileSizeBytes ?? null;
                                const sizeText = sizeBytes != null ? `${formatFileSize(sizeBytes)} · ` : "";
                                const dateText = uploadedAt ? formatUploadedAt(uploadedAt) : "Uploaded just now";
                                return `${sizeText}${dateText}`;
                            })()}
                        </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                        <button
                            type="button"
                            onClick={handlePreview}
                            disabled={!uploadData.file && !previewUrl}
                            aria-label={`Preview ${accessibleFieldName ?? displayName}`}
                            className="h-7 w-7 !p-2 flex items-center justify-center rounded-[6px] text-[#6a7785] hover:text-[#0054c7] hover:bg-[#f2f8ff] disabled:opacity-40"
                        >
                            <Eye size={18} strokeWidth={2} style={{ width: 18, height: 18, flexShrink: 0 }} />
                        </button>
                        <button
                            type="button"
                            onClick={deleteFile}
                            aria-label={`Remove ${accessibleFieldName ?? displayName}`}
                            className="h-7 w-7 !p-2 flex items-center justify-center rounded-[6px] text-[#df4f52] hover:text-red-700 hover:bg-red-100"
                        >
                            <X size={18} strokeWidth={2} style={{ width: 18, height: 18, flexShrink: 0 }} />
                        </button>
                    </div>
                </div>
            ) : (
                <div
                        className={className}
                >
                    <div className="flex">
                        <span>{uploadData.filename}</span>
                        <a
                            href="#"
                            onClick={deleteFile}
                                className=""
                        >
                            <span className="sr-only">Delete previously uploaded file {uploadData.filename}</span>
                            <div
                                className="ms-2 text-sm bg-blue-700 rounded-[15px] size-6 flex justify-center items-center">
                                <div
                                    aria-hidden
                                    className="text-sm text-white font-bold"
                                >
                                    x
                                </div>
                            </div>
                        </a>
                    </div>
                </div>
            ))
        ) ||
        <div
            className={className}
            onDrop={droppable ? handleDrop : undefined}
            onDragOver={droppable ? (e) => e.preventDefault() : undefined}
        >
            <input
                    id={id}
                    type="file"
                    className={inputClassName}
                    accept={accept}
                    onChange={handleChange}
                    value={uploadData.filename}
                    disabled={disabled}
            />
                <label htmlFor={id}>{fieldLabel}</label>
        </div>
    );
};

export default FileUpload;